import "./styles.scss";
import * as React from "react";
import { useState } from "react";
import { Modal, Image, Input, Icon, Card, Pagination, Dimmer, Loader } from "semantic-ui-react";
import { Blog, ContentPage, PlayerCardFormSummary, Post, Review, SmartLink } from "../../../services/bedrock";
import { ReviewSummaryLine } from "@/components/ShareDesignPost/ReviewPickerRow";
import { Svg } from "../../../assets";
import { RecordContextData, RecordContextType } from "../Contexts/RecordContext";
import FormRecordCard from "./FormRecordCard";
import pluralize from "pluralize";

interface RecordSelectorProps {
  value: number | null;
  context: RecordContextData<RecordContextType>;
  onChange: (value: number) => void;
}

const RecordSelector: React.FC<RecordSelectorProps> = ({ value, context: Context, onChange }) => {
  const [openModal, setOpenModal] = useState(false);
  const isPostType = Context.recordTypeLabel === "Post";
  const isSmartLinkType = Context.recordTypeLabel === "SmartLink";
  const isBlogType = Context.recordTypeLabel === "Blog";
  const isReviewType = Context.recordTypeLabel === "Review";
  const isFormType = Context.recordTypeLabel === "Form";
  const record = value ? Context.cachedRecords[value] : undefined;

  const recordPreview = (record: RecordContextType, inModal: boolean) => {
    const handleClick = () => {
      setOpenModal(prev => !prev);
      onChange(Number(record.id));
    };
    if (isReviewType) {
      return (
        <Card key={record.id} className="recordPreview reviewRecordPreview" onClick={handleClick}>
          <Card.Content>
            <ReviewSummaryLine review={record as Review} />
          </Card.Content>
        </Card>
      );
    }
    if (isFormType) {
      return (
        <FormRecordCard
          key={record.id}
          form={record as PlayerCardFormSummary}
          onClick={handleClick}
          variant={inModal ? "option" : "selected"}
          isCurrent={inModal && Number(record.id) === value}
        />
      );
    }
    const date = isPostType
      ? (record as Post).postDate
      : isSmartLinkType
      ? (record as SmartLink).createdDate
      : isBlogType
      ? (record as Blog).publishedAt || (record as Blog).createdDate
      : (record as ContentPage).createdDate;
    const message = isPostType
      ? (record as Post).message
      : isSmartLinkType
      ? (record as SmartLink).name
      : isBlogType
      ? (record as Blog).title
      : (record as ContentPage).title;
    const thumbnail = isSmartLinkType
      ? (record as SmartLink).ogImageUrl
      : "thumbnailUrl" in record
      ? record.thumbnailUrl
      : Svg.NoPreviewText;
    return (
      <Card key={record.id} className="recordPreview" onClick={handleClick}>
        <Image src={thumbnail} />
        <Card.Content>
          <Card.Description title={message}>{message}</Card.Description>
        </Card.Content>
        {date && (
          <Card.Content extra title={new Date(date).toLocaleString()}>
            {new Date(date).toDateString()}
          </Card.Content>
        )}
      </Card>
    );
  };

  return (
    <div className="playerCardRecordSelector">
      <Modal
        open={openModal}
        size="large"
        onClose={() => setOpenModal(false)}
        className="playerCardRecordSelectorModal"
        closeIcon={true}
      >
        <Modal.Header>Select {Context.recordTypeLabel}</Modal.Header>
        <Modal.Content className="playerCardRecordSelectorModalContent">
          <Input
            fluid
            placeholder="Search"
            defaultValue={Context.searchText}
            onChange={(_, data) => Context.setSearchText(data.value)}
          />
          {Context.isLoading && (
            <Dimmer active inverted>
              <Loader inverted>Loading</Loader>
            </Dimmer>
          )}
          {Context.records.length > 0 ? (
            <div
              className={
                isFormType
                  ? "grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3"
                  : isReviewType
                  ? "recordList reviewRecordList"
                  : "recordList"
              }
            >
              {Context.records.map(record => recordPreview(record, true))}
            </div>
          ) : (
            <div className="emptyRecordList">
              {!Context.isLoading && (
                <span className="noRecordsLabel">No {pluralize(Context.recordTypeLabel, 0)} found</span>
              )}
            </div>
          )}
          <Pagination
            className="playerCardRecordSelectorPagination"
            activePage={Context.activePage}
            boundaryRange={3}
            pointing
            secondary
            firstItem={null}
            lastItem={null}
            totalPages={Context.totalPages}
            disabled={Context.isLoading}
            onPageChange={(_, data) => Context.setActivePage(Number(data.activePage))}
          />
        </Modal.Content>
      </Modal>
      {record ? (
        recordPreview(record, false)
      ) : (
        <div className="selectRecord" title={`Select ${Context.recordTypeLabel}`} onClick={() => setOpenModal(true)}>
          <Icon name="add circle" size="huge" />
        </div>
      )}
    </div>
  );
};
export default RecordSelector;
